/**
 * Serviço de Geolocalização
 * Responsável por obter dados de localização geográfica através de diferentes métodos
 */
class GeolocationService {
    constructor(config = {}, logger = null) {
        this.config = {
            enableIPGeolocation: true,
            enableBrowserGeolocation: true,
            timeout: 5000,
            cacheEnabled: config.cacheEnabled !== false,
            cacheMaxAge: config.cacheMaxAge || 300000, // 5 minutos
            ...config
        };
        this.logger = logger || console;
        
        // Cache inteligente para posições
        this.positionCache = new Map();
        this.watcherCallbacks = new Map();
        
        this.log('GeolocationService inicializado com cache', 'info');
    }

    /**
     * Log de técnica utilizada
     * @param {string} technique Nome da técnica
     * @param {string} level Nível de detecção
     * @param {string} description Descrição da técnica
     */
    logTechnique(technique, level, description) {
        if (this.logger && typeof this.logger.debug === 'function') {
            this.logger.debug(`[${technique}] ${level}: ${description}`);
        }
    }

    /**
     * Log genérico
     * @param {string} message Mensagem
     * @param {string} level Nível do log
     */
    log(message, level = 'info') {
        if (this.logger && typeof this.logger[level] === 'function') {
            this.logger[level](message);
        } else if (this.logger && typeof this.logger.debug === 'function') {
            this.logger.debug(`[${level.toUpperCase()}] ${message}`);
        }
    }

    /**
     * Obter geolocalização através do IP público
     * @returns {Promise<Object>} Dados de geolocalização
     */
    async getIPGeolocation() {
        this.logTechnique('IP Geolocation', 'Alto', 'Obtém localização geográfica através do IP');
        console.log('[GeolocationService] Iniciando getIPGeolocation');
        
        try {
            // Lista de APIs gratuitas de geolocalização por IP
            const geoAPIs = [
                {
                    url: 'https://ipapi.co/json/',
                    parser: (data) => ({
                        ip: data.ip,
                        country: data.country_name,
                        countryCode: data.country_code,
                        region: data.region,
                        city: data.city,
                        latitude: data.latitude,
                        longitude: data.longitude,
                        timezone: data.timezone,
                        isp: data.org,
                        asn: data.asn,
                        postal: data.postal,
                        accuracy: 'city'
                    })
                },
                {
                    url: 'https://api.ipgeolocation.io/ipgeo?apiKey=free',
                    parser: (data) => ({
                        ip: data.ip,
                        country: data.country_name,
                        countryCode: data.country_code2,
                        region: data.state_prov,
                        city: data.city,
                        latitude: parseFloat(data.latitude),
                        longitude: parseFloat(data.longitude),
                        timezone: data.time_zone.name,
                        isp: data.isp,
                        asn: data.asn,
                        postal: data.zipcode,
                        accuracy: 'city'
                    })
                },
                {
                    url: 'http://ip-api.com/json/',
                    parser: (data) => ({
                        ip: data.query,
                        country: data.country,
                        countryCode: data.countryCode,
                        region: data.regionName,
                        city: data.city,
                        latitude: data.lat,
                        longitude: data.lon,
                        timezone: data.timezone,
                        isp: data.isp,
                        asn: data.as,
                        postal: data.zip,
                        accuracy: 'city'
                    })
                }
            ];

            // Tentar cada API até conseguir uma resposta válida
            console.log('[GeolocationService] Testando', geoAPIs.length, 'APIs de geolocalização');
            for (const api of geoAPIs) {
                try {
                    console.log('[GeolocationService] Tentando API:', api.url);
                    const response = await fetch(api.url, {
                        method: 'GET',
                        headers: {
                            'Accept': 'application/json'
                        },
                        signal: AbortSignal.timeout(this.config.timeout)
                    });

                    console.log('[GeolocationService] Response status:', response.status, 'ok:', response.ok);
                    if (response.ok) {
                        const data = await response.json();
                        console.log('[GeolocationService] Data recebida:', data);
                        const parsedData = api.parser(data);
                        console.log('[GeolocationService] Data parseada:', parsedData);
                        
                        // Validar se os dados essenciais estão presentes
                        if (parsedData.ip && parsedData.country) {
                            const result = {
                                ...parsedData,
                                source: api.url,
                                timestamp: new Date().toISOString(),
                                success: true,
                                technique: 'ip-geolocation'
                            };
                            console.log('[GeolocationService] Sucesso na API:', api.url, result);
                            return result;
                        } else {
                            console.log('[GeolocationService] Dados inválidos da API:', api.url, 'ip:', parsedData.ip, 'country:', parsedData.country);
                        }
                    }
                } catch (apiError) {
                    console.log('[GeolocationService] Erro na API:', api.url, apiError.message);
                    this.log(`Erro na API ${api.url}: ${apiError.message}`, 'warning');
                    continue;
                }
            }

            // Se todas as APIs falharam, retornar dados básicos
            return {
                ip: 'unknown',
                country: 'Unknown',
                countryCode: 'XX',
                region: 'Unknown',
                city: 'Unknown',
                latitude: null,
                longitude: null,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                isp: 'Unknown',
                asn: 'Unknown',
                postal: 'Unknown',
                accuracy: 'none',
                source: 'fallback',
                timestamp: new Date().toISOString(),
                success: false,
                error: 'Todas as APIs de geolocalização falharam',
                technique: 'ip-geolocation-fallback'
            };

        } catch (error) {
            return {
                ip: 'error',
                country: 'Error',
                countryCode: 'XX',
                region: 'Error',
                city: 'Error',
                latitude: null,
                longitude: null,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                isp: 'Error',
                asn: 'Error',
                postal: 'Error',
                accuracy: 'none',
                source: 'error',
                timestamp: new Date().toISOString(),
                success: false,
                error: error.message,
                technique: 'ip-geolocation-error'
            };
        }
    }

    /**
     * Obter geolocalização através do navegador (GPS/WiFi)
     * @returns {Promise<Object>} Dados de geolocalização do navegador
     */
    async getBrowserGeolocation() {
        this.logTechnique('Browser Geolocation', 'Alto', 'Obtém localização através da API do navegador');
        console.log('[GeolocationService] Iniciando getBrowserGeolocation');
        
        // Verificar se a geolocalização está disponível
        console.log('[GeolocationService] Verificando navigator.geolocation:', !!navigator.geolocation);
        if (!navigator.geolocation) {
            console.log('[GeolocationService] Geolocalização não suportada');
            return {
                success: false,
                error: 'Geolocalização não suportada pelo navegador',
                technique: 'browser-geolocation-unsupported'
            };
        }

        // Verificar se está habilitado na configuração
        console.log('[GeolocationService] Config enableBrowserGeolocation:', this.config.enableBrowserGeolocation);
        if (!this.config.enableBrowserGeolocation) {
            console.log('[GeolocationService] Geolocalização do navegador desabilitada');
            return {
                success: false,
                error: 'Geolocalização do navegador desabilitada na configuração',
                technique: 'browser-geolocation-disabled'
            };
        }

        // Verificar cache primeiro
        const cacheKey = 'browser-geolocation';
        const cachedPosition = this.getCachedPosition(cacheKey);
        if (cachedPosition) {
            this.log('Usando posição do cache', 'info');
            return cachedPosition;
        }

        return new Promise((resolve) => {
            // Obter recomendações baseadas na conexão
            const connectionInfo = this.getConnectionInfo();
            const recommendations = connectionInfo.recommendations;
            
            // Opções otimizadas baseadas na qualidade da conexão
            const options = {
                enableHighAccuracy: recommendations.enableHighAccuracy,
                timeout: this.config.timeout || recommendations.timeout,
                maximumAge: recommendations.maximumAge
            };
            
            this.log(`Usando configurações otimizadas: ${recommendations.reason}`, 'info');

            console.log('[GeolocationService] Chamando getCurrentPosition com options:', options);
            
            // Tentar primeiro com alta precisão
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    console.log('[GeolocationService] Sucesso na geolocalização do navegador (alta precisão):', position);
                    const result = this.formatGeolocationResult(position, 'browser-geolocation-high-accuracy');
                    console.log('[GeolocationService] Resultado da geolocalização do navegador:', result);
                    
                    // Armazenar no cache
                    this.cachePosition(cacheKey, result);
                    
                    resolve(result);
                },
                (error) => {
                    console.log('[GeolocationService] Erro na geolocalização de alta precisão:', error.code, error.message);
                    
                    // Fallback para baixa precisão se alta precisão falhar
                    const fallbackOptions = {
                        enableHighAccuracy: false,
                        timeout: 10000,
                        maximumAge: 300000 // 5 minutos
                    };
                    
                    console.log('[GeolocationService] Tentando fallback com baixa precisão:', fallbackOptions);
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            console.log('[GeolocationService] Sucesso na geolocalização do navegador (baixa precisão):', position);
                            const result = this.formatGeolocationResult(position, 'browser-geolocation-low-accuracy');
                            console.log('[GeolocationService] Resultado do fallback:', result);
                            
                            // Armazenar no cache
                            this.cachePosition(cacheKey, result);
                            
                            resolve(result);
                        },
                        (fallbackError) => {
                            console.log('[GeolocationService] Erro no fallback:', fallbackError.code, fallbackError.message);
                            this.log(`Erro ao obter geolocalização do navegador: ${fallbackError.message}`, 'warning');
                            const errorResult = {
                                success: false,
                                error: fallbackError.message,
                                code: fallbackError.code,
                                technique: 'browser-geolocation-error',
                                details: this.getGeolocationErrorDetails(fallbackError.code)
                            };
                            console.log('[GeolocationService] Resultado de erro final:', errorResult);
                            resolve(errorResult);
                        },
                        fallbackOptions
                    );
                },
                options
            );
        });
    }

    /**
     * Inicia monitoramento contínuo da geolocalização
     * @param {Function} callback - Callback chamado a cada atualização
     * @param {Object} options - Opções de configuração
     * @returns {number} ID do watcher para poder parar depois
     */
    startWatchingPosition(callback, options = {}) {
        if (!navigator.geolocation) {
            this.log('Geolocalização não suportada para watchPosition', 'warning');
            return null;
        }

        const watchOptions = {
            enableHighAccuracy: options.enableHighAccuracy || true,
            timeout: options.timeout || 30000,
            maximumAge: options.maximumAge || 30000 // 30 segundos
        };

        this.log('Iniciando monitoramento contínuo da geolocalização', 'info');
        
        const watchId = navigator.geolocation.watchPosition(
            (position) => {
                const result = this.formatGeolocationResult(position, 'browser-geolocation-watch');
                this.log('Nova posição obtida via watchPosition', 'info');
                callback(result);
            },
            (error) => {
                const errorResult = {
                    success: false,
                    error: error.message,
                    code: error.code,
                    technique: 'browser-geolocation-watch-error',
                    details: this.getGeolocationErrorDetails(error.code)
                };
                this.log(`Erro no watchPosition: ${error.message}`, 'warning');
                callback(errorResult);
            },
            watchOptions
        );

        return watchId;
    }

    /**
     * Para o monitoramento contínuo da geolocalização
     * @param {number} watchId - ID do watcher retornado por startWatchingPosition
     */
    stopWatchingPosition(watchId) {
        if (watchId && navigator.geolocation) {
            navigator.geolocation.clearWatch(watchId);
            this.log('Monitoramento de geolocalização parado', 'info');
        }
    }

    /**
     * Obter dados completos de geolocalização (IP + Navegador)
     * @returns {Promise<Object>} Dados combinados de geolocalização
     */
    async getCompleteGeolocation() {
        this.logTechnique('Complete Geolocation', 'Alto', 'Obtém dados completos de geolocalização');
        console.log('[GeolocationService] Iniciando coleta completa de geolocalização');
        console.log('[GeolocationService] Config:', this.config);
        
        const results = {
            timestamp: new Date().toISOString(),
            technique: 'complete-geolocation'
        };

        // Executar ambas as coletas em paralelo
        console.log('[GeolocationService] Executando coletas em paralelo...');
        const [ipGeo, browserGeo] = await Promise.allSettled([
            this.config.enableIPGeolocation ? this.getIPGeolocation() : Promise.resolve(null),
            this.config.enableBrowserGeolocation ? this.getBrowserGeolocation() : Promise.resolve(null)
        ]);
        
        console.log('[GeolocationService] Resultados das coletas:', { ipGeo, browserGeo });

        // Processar resultado da geolocalização por IP
        console.log('[GeolocationService] Processando resultado IP:', ipGeo);
        if (ipGeo.status === 'fulfilled' && ipGeo.value) {
            results.ipGeolocation = ipGeo.value;
            console.log('[GeolocationService] IP Geolocation sucesso:', ipGeo.value);
        } else {
            results.ipGeolocation = {
                success: false,
                error: 'Falha na geolocalização por IP',
                technique: 'ip-geolocation-failed'
            };
            console.log('[GeolocationService] IP Geolocation falhou:', ipGeo.reason);
        }

        // Processar resultado da geolocalização do navegador
        console.log('[GeolocationService] Processando resultado Browser:', browserGeo);
        if (browserGeo.status === 'fulfilled' && browserGeo.value) {
            results.browserGeolocation = browserGeo.value;
            console.log('[GeolocationService] Browser Geolocation sucesso:', browserGeo.value);
        } else {
            results.browserGeolocation = {
                success: false,
                error: 'Falha na geolocalização do navegador',
                technique: 'browser-geolocation-failed'
            };
            console.log('[GeolocationService] Browser Geolocation falhou:', browserGeo.reason);
        }

        // Calcular consistência entre as duas fontes
        if (results.ipGeolocation.success && results.browserGeolocation.success) {
            results.consistency = this.calculateLocationConsistency(
                results.ipGeolocation,
                results.browserGeolocation
            );
        }

        // Implementar fallback melhorado quando ambas falharem
        if (!results.ipGeolocation.success && !results.browserGeolocation.success) {
            console.log('[GeolocationService] Ambas geolocalizações falharam, aplicando fallback');
            results.fallback = this.getFallbackGeolocation();
        }

        console.log('[GeolocationService] Resultado final:', results);
        return results;
    }

    /**
     * Calcular consistência entre duas localizações
     * @param {Object} ipGeo Dados de geolocalização por IP
     * @param {Object} browserGeo Dados de geolocalização do navegador
     * @returns {Object} Análise de consistência
     */
    calculateLocationConsistency(ipGeo, browserGeo) {
        if (!ipGeo.latitude || !ipGeo.longitude || !browserGeo.latitude || !browserGeo.longitude) {
            return {
                consistent: false,
                reason: 'Coordenadas incompletas',
                distance: null
            };
        }

        // Calcular distância usando fórmula de Haversine
        const distance = this.calculateDistance(
            ipGeo.latitude, ipGeo.longitude,
            browserGeo.latitude, browserGeo.longitude
        );

        // Considerar consistente se a distância for menor que 100km
        const consistent = distance < 100;

        return {
            consistent,
            distance: Math.round(distance * 100) / 100, // Arredondar para 2 casas decimais
            reason: consistent ? 'Localizações próximas' : 'Localizações distantes',
            ipLocation: `${ipGeo.city}, ${ipGeo.country}`,
            browserAccuracy: browserGeo.accuracy
        };
    }

    /**
     * Calcular distância entre duas coordenadas usando fórmula de Haversine
     * @param {number} lat1 Latitude 1
     * @param {number} lon1 Longitude 1
     * @param {number} lat2 Latitude 2
     * @param {number} lon2 Longitude 2
     * @returns {number} Distância em quilômetros
     */
    calculateDistance(lat1, lon1, lat2, lon2) {
        const R = 6371; // Raio da Terra em km
        const dLat = this.toRadians(lat2 - lat1);
        const dLon = this.toRadians(lon2 - lon1);
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
                  Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    /**
     * Converter graus para radianos
     * @param {number} degrees Graus
     * @returns {number} Radianos
     */
    toRadians(degrees) {
        return degrees * (Math.PI / 180);
    }

    /**
     * Obter informações de timezone
     * @returns {Object} Dados de timezone
     */
    getTimezoneInfo() {
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const offset = new Date().getTimezoneOffset();
            
            return {
                timezone,
                offset: -offset, // Inverter sinal para obter offset correto
                offsetString: this.formatTimezoneOffset(-offset),
                technique: 'timezone-info'
            };
        } catch (error) {
            return {
                timezone: 'Unknown',
                offset: 0,
                offsetString: '+00:00',
                error: error.message,
                technique: 'timezone-info-error'
            };
        }
    }

    /**
     * Formatar offset de timezone
     * @param {number} offset Offset em minutos
     * @returns {string} Offset formatado
     */
    formatTimezoneOffset(offset) {
        const hours = Math.floor(Math.abs(offset) / 60);
        const minutes = Math.abs(offset) % 60;
        const sign = offset >= 0 ? '+' : '-';
        return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    /**
     * Fallback quando ambas as geolocalizações falharem
     * Fornece dados básicos disponíveis no navegador
     */
    getFallbackGeolocation() {
        console.log('[GeolocationService] Executando fallback geolocation');
        
        const fallbackData = {
            success: true,
            technique: 'fallback',
            timestamp: new Date().toISOString(),
            source: 'browser-info',
            data: {
                // Informações de timezone
                timezone: this.getTimezoneInfo(),
                
                // Informações do navegador que podem indicar localização
                language: navigator.language || navigator.userLanguage,
                languages: navigator.languages || [navigator.language],
                
                // Informações de conexão se disponíveis
                connection: this.getConnectionInfo(),
                
                // Estimativa básica baseada em timezone
                estimatedLocation: this.estimateLocationFromTimezone()
            },
            confidence: 'low',
            note: 'Dados estimados - geolocalização precisa não disponível'
        };

        console.log('[GeolocationService] Fallback data:', fallbackData);
        return fallbackData;
    }

    /**
     * Formata o resultado da geolocalização do navegador
     * @param {GeolocationPosition} position - Posição obtida do navegador
     * @param {string} technique - Técnica utilizada
     * @returns {Object} Resultado formatado
     */
    formatGeolocationResult(position, technique) {
        return {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
            timestamp: new Date(position.timestamp).toISOString(),
            success: true,
            technique: technique
        };
    }

    /**
     * Obtém detalhes do erro de geolocalização
     * @param {number} errorCode - Código do erro
     * @returns {Object} Detalhes do erro
     */
    getGeolocationErrorDetails(errorCode) {
        const errorDetails = {
            1: {
                name: 'PERMISSION_DENIED',
                description: 'Usuário negou a permissão de geolocalização',
                suggestion: 'Solicite ao usuário para permitir acesso à localização'
            },
            2: {
                name: 'POSITION_UNAVAILABLE',
                description: 'Localização não disponível',
                suggestion: 'Verifique conexão de rede e GPS'
            },
            3: {
                name: 'TIMEOUT',
                description: 'Timeout na obtenção da localização',
                suggestion: 'Tente novamente ou aumente o timeout'
            }
        };
        
        return errorDetails[errorCode] || {
            name: 'UNKNOWN_ERROR',
            description: 'Erro desconhecido',
            suggestion: 'Tente novamente'
        };
    }

    /**
     * Armazena uma posição válida no cache
     * @param {string} key - Chave do cache
     * @param {Object} position - Posição a ser armazenada
     */
    cachePosition(key, position) {
        if (!this.config.cacheEnabled || !position.success) {
            return;
        }

        const cacheEntry = {
            position: position,
            timestamp: Date.now(),
            accuracy: position.accuracy || 0
        };

        this.positionCache.set(key, cacheEntry);
        this.log(`Posição armazenada no cache: ${key}`, 'info');
        
        // Limpar cache antigo
        this.cleanExpiredCache();
    }

    /**
     * Recupera uma posição do cache se ainda válida
     * @param {string} key - Chave do cache
     * @returns {Object|null} Posição em cache ou null
     */
    getCachedPosition(key) {
        if (!this.config.cacheEnabled) {
            return null;
        }

        const cacheEntry = this.positionCache.get(key);
        if (!cacheEntry) {
            return null;
        }

        const age = Date.now() - cacheEntry.timestamp;
        if (age > this.config.cacheMaxAge) {
            this.positionCache.delete(key);
            this.log(`Cache expirado removido: ${key}`, 'info');
            return null;
        }

        this.log(`Posição recuperada do cache: ${key}`, 'info');
        return {
            ...cacheEntry.position,
            fromCache: true,
            cacheAge: age
        };
    }

    /**
     * Limpa entradas expiradas do cache
     */
    cleanExpiredCache() {
        const now = Date.now();
        let cleanedCount = 0;

        for (const [key, entry] of this.positionCache.entries()) {
            if (now - entry.timestamp > this.config.cacheMaxAge) {
                this.positionCache.delete(key);
                cleanedCount++;
            }
        }

        if (cleanedCount > 0) {
            this.log(`Cache limpo: ${cleanedCount} entradas removidas`, 'info');
        }
    }

    /**
     * Obtém informações detalhadas de conexão usando Network Information API
     * @returns {Object} Informações de conexão e recomendações
     */
    getConnectionInfo() {
        this.log('Coletando informações de conexão', 'info');
        
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        
        const connectionInfo = {
            effectiveType: connection?.effectiveType || 'unknown',
            downlink: connection?.downlink || null,
            rtt: connection?.rtt || null,
            saveData: connection?.saveData || false,
            type: connection?.type || 'unknown',
            supported: !!connection
        };

        // Adicionar recomendações baseadas na conexão
        connectionInfo.recommendations = this.getConnectionRecommendations(connectionInfo);
        
        return connectionInfo;
    }

    /**
     * Obtém recomendações baseadas na qualidade da conexão
     * @param {Object} connectionInfo - Informações de conexão
     * @returns {Object} Recomendações de configuração
     */
    getConnectionRecommendations(connectionInfo) {
        const recommendations = {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 60000,
            preferBrowserGeolocation: true,
            reason: 'default'
        };

        // Ajustar baseado no tipo de conexão
        switch (connectionInfo.effectiveType) {
            case 'slow-2g':
            case '2g':
                recommendations.enableHighAccuracy = false;
                recommendations.timeout = 30000;
                recommendations.maximumAge = 600000; // 10 minutos
                recommendations.preferBrowserGeolocation = false;
                recommendations.reason = 'conexão lenta detectada';
                break;
                
            case '3g':
                recommendations.timeout = 20000;
                recommendations.maximumAge = 300000; // 5 minutos
                recommendations.reason = 'conexão 3G detectada';
                break;
                
            case '4g':
            case '5g':
                recommendations.enableHighAccuracy = true;
                recommendations.timeout = 10000;
                recommendations.maximumAge = 30000; // 30 segundos
                recommendations.reason = 'conexão rápida detectada';
                break;
        }

        // Ajustar se saveData estiver ativo
        if (connectionInfo.saveData) {
            recommendations.enableHighAccuracy = false;
            recommendations.maximumAge = 600000; // 10 minutos
            recommendations.preferBrowserGeolocation = false;
            recommendations.reason += ' + modo economia de dados ativo';
        }

        // Ajustar baseado no RTT se disponível
        if (connectionInfo.rtt) {
            if (connectionInfo.rtt > 1000) { // RTT alto
                recommendations.timeout = Math.max(recommendations.timeout, 25000);
                recommendations.enableHighAccuracy = false;
                recommendations.reason += ' + alta latência detectada';
            }
        }

        return recommendations;
    }

    /**
     * Estima localização básica baseada no timezone
     */
    estimateLocationFromTimezone() {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        // Mapeamento básico de timezones para regiões
        const timezoneRegions = {
            'America/Sao_Paulo': { country: 'BR', region: 'South America' },
            'America/New_York': { country: 'US', region: 'North America' },
            'Europe/London': { country: 'GB', region: 'Europe' },
            'Europe/Paris': { country: 'FR', region: 'Europe' },
            'Asia/Tokyo': { country: 'JP', region: 'Asia' },
            'Australia/Sydney': { country: 'AU', region: 'Oceania' }
        };

        const estimated = timezoneRegions[timezone] || {
            country: 'Unknown',
            region: 'Unknown'
        };

        return {
            timezone: timezone,
            estimatedCountry: estimated.country,
            estimatedRegion: estimated.region,
            method: 'timezone-mapping'
        };
    }
}

export default GeolocationService;